import { Stack, Typography, Tooltip, Avatar, Popper, Paper, ClickAwayListener } from '@mui/material'
import { styled } from '@mui/material/styles'
import IconButton from 'components/@extended/IconButton'
import { LogoutOutlined } from '@ant-design/icons'
import useAuth from 'hooks/useAuth'
import { useNavigate } from 'react-router'
import { useTheme } from '@mui/material'
import { useState } from 'react'
import Transitions from 'components/@extended/Transitions'

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 200,
  borderRadius: '5px',
}))

const createStyles = (theme) => ({
  logout: {
    p: '12px',
    borderTop: `1px solid ${theme.palette.secondary.main}`,
  },
  badge: {
    borderRadius: '5px',
    height: '5px',
    width: '5px',
  },
  avatar: {
    width: 28,
    height: 28,
    backgroundColor: theme.palette.secondary.darker,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
})

const UserInfoContent = ({ user, userTenant, onLogout, styles, theme }) => (
  <Stack>
    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Typography sx={{ lineHeight: '1.25rem' }} color={theme.palette.text.secondary}>
          {user?.firstName} {user?.lastName}
        </Typography>
      </Stack>
      <Tooltip title="Logout">
        <IconButton size="small" color="text.secondary" onClick={onLogout}>
          <LogoutOutlined />
        </IconButton>
      </Tooltip>
    </Stack>
    <Stack direction="row" gap={0.5} alignItems="center">
      <span style={{ ...styles.badge, backgroundColor: user?.preferences?.color }} />
      <Typography variant="subtitle2" sx={{ lineHeight: '1.125rem' }}>
        {userTenant}
      </Typography>
    </Stack>
  </Stack>
)

const DrawerFooter = ({ open }) => {
  const theme = useTheme()
  const styles = createStyles(theme)
  const { userTenant, user, logout } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleHover = (event) => {
    if (open) return
    setAnchorEl(anchorEl ? null : event?.currentTarget)
  }

  const handleClose = () => {
    if (open) return
    setAnchorEl(null)
  }

  const popperOpen = Boolean(anchorEl)

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login', { state: { from: '' } })
    } catch (err) {
      console.error(err)
    }
  }

  if (!userTenant && !user) return null

  return (
    <Stack sx={styles.logout} onMouseEnter={handleHover} onMouseLeave={handleClose}>
      {open ? (
        <UserInfoContent
          user={user}
          userTenant={userTenant}
          onLogout={handleLogout}
          styles={styles}
          theme={theme}
        />
      ) : (
        <>
          <Avatar alt={`${user?.firstName} ${user?.lastName}`} sx={styles.avatar}>
            {user?.firstName[0]}
            {user?.lastName[0]}
          </Avatar>
          <PopperStyled
            open={popperOpen}
            anchorEl={anchorEl}
            placement="right-end"
            style={{
              zIndex: 2001,
            }}
            popperOptions={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 2],
                  },
                },
              ],
            }}
          >
            {({ TransitionProps }) => (
              <Transitions in={popperOpen} {...TransitionProps}>
                <Paper
                  sx={{
                    overflow: 'hidden',
                    p: 2,
                    boxShadow: theme.shadows[8],
                    backgroundImage: 'none',
                  }}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <div>
                      <UserInfoContent
                        user={user}
                        userTenant={userTenant}
                        onLogout={handleLogout}
                        styles={styles}
                        theme={theme}
                      />
                    </div>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </PopperStyled>
        </>
      )}
    </Stack>
  )
}

export default DrawerFooter
